import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';
import { onReviewMoment, type ReviewMoment } from '@/lib/meaningfulActions';
import {
  getEligibility, getPromptState, isQuiet, majorPromptAllowed, markMajorPrompt, REVIEW_COOLDOWN_MS,
} from '@/lib/promptCoordinator';
import { track } from '@/lib/analytics';

const MIN_AGE_DAYS = 7;
const MIN_ACTIVE_DAYS = 4;
const MIN_ACTIONS = 3;

/**
 * Signals Apple/Google that this is a good moment for a review, using the
 * official in-app review API only (no custom rating gate). The OS decides
 * whether the sheet actually appears.
 */
export const useReviewRequest = () => {
  const { user } = useAuth();
  const busy = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user) return;
    return onReviewMoment((moment: ReviewMoment) => {
      if (busy.current || isQuiet()) return;
      busy.current = true;
      void (async () => {
        try {
          const state = await getPromptState(user.id);
          if (!majorPromptAllowed(state)) return;
          if (state.last_review_request_at && Date.now() - new Date(state.last_review_request_at).getTime() < REVIEW_COOLDOWN_MS) return;
          const elig = await getEligibility();
          if (!elig) return;
          if (elig.account_age_days < MIN_AGE_DAYS || elig.active_days < MIN_ACTIVE_DAYS || elig.meaningful_action_count < MIN_ACTIONS) return;

          let appVersion: string | null = null;
          try {
            const { App } = await import('@capacitor/app');
            appVersion = (await App.getInfo()).version;
          } catch { /* noop */ }
          const props = {
            trigger_source: moment,
            account_age_days: Math.floor(elig.account_age_days),
            active_days: elig.active_days,
            meaningful_action_count: elig.meaningful_action_count,
            app_version: appVersion,
            platform: Capacitor.getPlatform(),
          };
          track('review_request_eligible', props);

          // Small pause so we land on a calm screen after the moment closes.
          await new Promise((r) => setTimeout(r, 800));
          const { InAppReview } = await import('@capacitor-community/in-app-review');
          await InAppReview.requestReview();
          track('review_request_attempted', props);
          await markMajorPrompt(user.id, {
            last_review_request_at: new Date().toISOString(),
            review_request_count: state.review_request_count + 1,
            app_version_at_request: appVersion,
          });
        } catch (e) {
          console.warn('[Review] request failed', e);
        } finally {
          busy.current = false;
        }
      })();
    });
  }, [user]);
};
